import React from 'react';
import { ProductsProvider } from 'contexts/ProductsProvider/ProductsProvider';
import { Text } from 'atoms/Text/Text';
import { defaultTheme } from 'constants/theme/defaultTheme';
import { Styles } from './styles';
import { AreaOfSpecialisationFilter } from './components/AreaOfSpecialisationFilter/AreaOfSpecialisationFilter';
import { ProductFilters } from './components/ProductFilters/ProductFilters';
import { useRouter } from 'next/router';
import DefaultArrow from 'assets/svgs/arrow.svg';
import { ProductsWrapper } from '../components/ProductsWrapper/ProductsWrapper';
import { GridItem } from 'components/Grid/Grid';
import { Hide } from 'atoms/Hide/Hide';
import { breakpoints } from 'stylesheet';
import { connectStateResults } from 'react-instantsearch-dom';
import { StateResultsProvided } from 'react-instantsearch-core';
import { EmptyState } from './components/EmptyState/EmptyState';
import { Hits } from './components/Hits/Hits';
import { ProductsContent } from 'features/products/components/ProductsContent/ProductsContent';
import { ScrollTopOnSearchChange } from './components/ScrollTopOnSearchChange/ScrollTopOnSearchChange';
import { useShowBackButton } from './hooks/useShowBackButton';

const newSearchHeading = 'Start searching for products';
const newSearchBody =
  'Search using product codes, product names, brand names, company name, product sizes or product categories';
const noResultHeading = (query: string) => `Didn’t find anything for ”${query}”`;
const noResultBody = `Try searching for another product`;

export const SearchScreenConnected: React.FC<StateResultsProvided> = props => {
  const { back } = useRouter();

  const { query } = props.searchState;
  const showBackButton = useShowBackButton();

  const noQueryProvided = query === undefined || query.length === 0;
  const noResultsReturned = props.searchResults?.nbHits === 0 ?? true;
  const showEmptyState = noQueryProvided || noResultsReturned;

  return (
    <ProductsWrapper>
      <ProductsContent>
        <ScrollTopOnSearchChange />
        <Styles.GridContainer>
          <GridItem xs={12} sm={4}>
            <Styles.Sidebar>
              <Styles.SideBarContent>
                <Hide when={showEmptyState} maxWidth={breakpoints.small}>
                  <Styles.SideBarFixedHeading isBorder={!noResultsReturned}>
                    <Styles.BackButton onClick={back}>
                      {showBackButton ? (
                        <>
                          <Styles.BackArrow>
                            <DefaultArrow />
                          </Styles.BackArrow>
                          <Text.body1 color={defaultTheme.colors.healthBlue}>
                            Back to product page
                          </Text.body1>
                        </>
                      ) : null}
                    </Styles.BackButton>
                    {!noResultsReturned && <AreaOfSpecialisationFilter />}
                  </Styles.SideBarFixedHeading>
                </Hide>
                <Styles.SideBarFilters>
                  <ProductFilters />
                </Styles.SideBarFilters>
              </Styles.SideBarContent>
            </Styles.Sidebar>
          </GridItem>
          <GridItem xs={12} sm={8}>
            <Styles.MainContent>
              <Styles.MainContentHeader>
                {!showEmptyState && <Text.body1>Search results</Text.body1>}
              </Styles.MainContentHeader>
              {noQueryProvided ? (
                <EmptyState heading={newSearchHeading} body={newSearchBody} />
              ) : noResultsReturned ? (
                <EmptyState heading={noResultHeading(query)} body={noResultBody} />
              ) : (
                <Hits />
              )}
            </Styles.MainContent>
          </GridItem>
        </Styles.GridContainer>
      </ProductsContent>
    </ProductsWrapper>
  );
};

const SearchScreen = connectStateResults(SearchScreenConnected);

export const Search = () => {
  return (
    <ProductsProvider>
      <SearchScreen />
    </ProductsProvider>
  );
};
